import { useState } from 'react';
import { BellRing, PhoneMissed, RefreshCw, TrendingDown, TrendingUp, UserCheck } from 'lucide-react';
import toast from 'react-hot-toast';
import { Card } from '@/components/ui/Card';
import { useMutation, useQueryState } from '@/lib/api-hooks';
import { backendApi } from '@/lib/backend';
import { useCrm } from './CrmContext';
import { buttonClass, errorMessage, formatWhen } from './format';

type Insight = {
  id: string;
  kind: 'going_quiet' | 'call_spike' | 'missed_often' | 'owner_suggestion';
  title: string;
  detail: string;
  contactId?: string;
  companyId?: string;
  suggestedExtension?: string;
  suggestedName?: string;
  detectedAtMs: number;
  status: 'open' | 'dismissed' | 'done';
};

const KIND = {
  going_quiet: { icon: TrendingDown, label: 'Gone quiet', color: 'text-amber-600 bg-amber-50 dark:bg-amber-900/30' },
  call_spike: { icon: TrendingUp, label: 'Call spike', color: 'text-red-600 bg-red-50 dark:bg-red-900/30' },
  missed_often: { icon: PhoneMissed, label: 'Often missed', color: 'text-red-600 bg-red-50 dark:bg-red-900/30' },
  owner_suggestion: { icon: UserCheck, label: 'Account owner', color: 'text-primary-600 bg-primary-50 dark:bg-primary-900/30' },
} as const;

export function InsightsTab() {
  const { data, openContact, openCompany } = useCrm();
  const [status, setStatus] = useState<'open' | 'dismissed' | 'done'>('open');
  const insights = useQueryState<Insight[]>(backendApi.crmInsights.listInsights, { status });
  const act = useMutation(backendApi.crmInsights.actOnInsight);
  const refresh = useMutation(backendApi.crmInsights.refreshInsightsNow);

  const run = async (insightId: string, action: 'dismiss' | 'create_task' | 'assign_owner', message: string) => {
    try {
      await act({ insightId, action });
      toast.success(message);
    } catch (error) {
      toast.error(errorMessage(error));
    }
  };

  return (
    <Card animate={false}>
      <div className="flex flex-wrap items-center gap-3 mb-2">
        <div className="flex gap-1 p-1 rounded-lg bg-gray-100 dark:bg-gray-700/50">
          {(['open', 'done', 'dismissed'] as const).map((value) => (
            <button
              type="button"
              key={value}
              onClick={() => setStatus(value)}
              className={`px-3 py-1.5 rounded-md text-sm font-medium ${status === value ? 'bg-white dark:bg-gray-800 shadow text-gray-900 dark:text-white' : 'text-gray-600 dark:text-gray-300'}`}
            >
              {value === 'open' ? 'Open' : value === 'done' ? 'Acted on' : 'Dismissed'}
            </button>
          ))}
        </div>
        {data?.canManage && (
          <button
            type="button"
            onClick={() =>
              void refresh({})
                .then(() => toast.success('Checking now; results appear in a minute'))
                .catch((error) => toast.error(errorMessage(error)))
            }
            className={`${buttonClass.secondary} ml-auto`}
          >
            <RefreshCw className="w-4 h-4" /> Check now
          </button>
        )}
      </div>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
        Checked every night from the last four months of calls with named contacts and companies.
        {data?.settings.autoAssignOwner ? ' Account owners are assigned automatically.' : ''}
      </p>

      {insights.error && <p className="text-red-600 text-sm">{insights.error}</p>}
      {insights.isLoading && <p className="text-gray-500 text-sm">Loading…</p>}
      {insights.data?.length === 0 && (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
          <BellRing className="w-10 h-10 mx-auto mb-2 opacity-60" />
          Nothing to flag right now.
        </div>
      )}
      <div className="space-y-2">
        {insights.data?.map((insight) => {
          const meta = KIND[insight.kind];
          const Icon = meta.icon;
          return (
            <div key={insight.id} className="flex flex-col sm:flex-row sm:items-center gap-3 p-3 rounded-lg border border-gray-200 dark:border-gray-700">
              <div className={`w-9 h-9 shrink-0 rounded-full flex items-center justify-center ${meta.color}`}>
                <Icon className="w-4 h-4" />
              </div>
              <button
                type="button"
                onClick={() => (insight.companyId ? openCompany(insight.companyId) : insight.contactId ? openContact(insight.contactId) : undefined)}
                className="min-w-0 flex-1 text-left"
              >
                <p className="font-medium text-gray-900 dark:text-white">{insight.title}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {meta.label} · {insight.detail} · {formatWhen(insight.detectedAtMs)}
                </p>
              </button>
              {data?.canManage && status === 'open' && (
                <div className="flex gap-1.5">
                  {insight.kind === 'owner_suggestion' ? (
                    <button type="button" onClick={() => void run(insight.id, 'assign_owner', 'Owner assigned')} className={buttonClass.small}>
                      Make owner
                    </button>
                  ) : (
                    <button type="button" onClick={() => void run(insight.id, 'create_task', 'Follow-up added')} className={buttonClass.small}>
                      Follow up
                    </button>
                  )}
                  <button type="button" onClick={() => void run(insight.id, 'dismiss', 'Dismissed for 30 days')} className={buttonClass.small}>
                    Dismiss
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Card>
  );
}
